import React, { useEffect, useState } from 'react';
import { Box, Flex, Spinner, Text, VStack } from '@chakra-ui/react';
import type { WalletPayoutDetailResponse } from '@foodfluence/shared';
import { walletApi } from '../../../services/api/wallet';
import { formatWalletBalance } from '../utils/walletUtils';
import {
  formatWithdrawalStatusLabel,
  formatWithdrawalSubtitle,
  formatWithdrawalTitle,
  withdrawalStatusColor,
} from '../utils/walletWithdrawalsUtils';
import ModalShell from '../../../components/ui/ModalShell';
import { MoneyFigure, Pill } from '../../../components/ui';
import { ff } from '../../../theme';

const STATUS_PILL_TONE: Record<string, 'ink' | 'lime' | 'grey'> = { green: 'lime', blue: 'ink', gray: 'grey' };

export interface WalletPayoutDetailModalProps {
  isOpen: boolean;
  payoutId: string | null;
  influencerId?: string;
  onClose: () => void;
}

const WalletPayoutDetailModal: React.FC<WalletPayoutDetailModalProps> = ({
  isOpen,
  payoutId,
  influencerId,
  onClose,
}) => {
  const [detail, setDetail] = useState<WalletPayoutDetailResponse | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!isOpen || !payoutId || !influencerId) {
      setDetail(null);
      setError(null);
      return;
    }

    let cancelled = false;
    setLoading(true);
    setError(null);

    walletApi
      .getPayoutDetail(payoutId, { influencerId })
      .then((res) => {
        if (!cancelled) {
          setDetail(res);
        }
      })
      .catch((err) => {
        if (!cancelled) {
          setDetail(null);
          setError(err instanceof Error ? err.message : 'Failed to load payout details');
        }
      })
      .finally(() => {
        if (!cancelled) {
          setLoading(false);
        }
      });

    return () => {
      cancelled = true;
    };
  }, [isOpen, payoutId, influencerId]);

  const statusColor = detail ? withdrawalStatusColor(detail.status) : 'gray';

  return (
    <ModalShell
      isOpen={isOpen}
      onClose={onClose}
      title={detail ? formatWithdrawalTitle(detail.destinationLabel) : 'Withdrawal details'}
    >
      {loading ? (
        <Flex align="center" justify="center" gap={2} py={8}>
          <Spinner size="sm" color={ff.ink} />
          <Text fontSize="14px" color={ff.text2}>
            Loading details…
          </Text>
        </Flex>
      ) : error ? (
        <Text fontSize="14px" color={ff.danger}>
          {error}
        </Text>
      ) : detail ? (
        <VStack align="stretch" spacing={4}>
          <MoneyFigure value={formatWalletBalance(detail.amount)} label="Amount" size="40px" />
          <VStack
            align="stretch"
            spacing={0}
            border="1px solid"
            borderColor={ff.line}
            borderRadius="20px"
            px={4}
            divider={<Box h="1px" bg={ff.line} border="none" />}
          >
            <Flex justify="space-between" align="center" minH="48px" gap={3}>
              <Text fontSize="14px" color={ff.text2}>
                Net to bank
              </Text>
              <Text textStyle="display" fontSize="17px" color={ff.ink}>
                {formatWalletBalance(detail.netAmount)}
              </Text>
            </Flex>
            {detail.feeAmount > 0 && (
              <Flex justify="space-between" align="center" minH="48px" gap={3}>
                <Text fontSize="14px" color={ff.text2}>
                  Fee
                </Text>
                <Text textStyle="display" fontSize="17px" color={ff.ink}>
                  {formatWalletBalance(detail.feeAmount)}
                </Text>
              </Flex>
            )}
            <Flex justify="space-between" align="center" minH="48px" gap={3}>
              <Text fontSize="14px" color={ff.text2}>
                Status
              </Text>
              {statusColor === 'red' ? (
                <Text fontSize="14px" fontWeight="600" color={ff.danger}>
                  {formatWithdrawalStatusLabel(detail.status)}
                </Text>
              ) : (
                <Pill tone={STATUS_PILL_TONE[statusColor] ?? 'grey'}>
                  {formatWithdrawalStatusLabel(detail.status)}
                </Pill>
              )}
            </Flex>
            {detail.estimatedArrival && (
              <Flex justify="space-between" align="center" minH="48px" gap={3}>
                <Text fontSize="14px" color={ff.text2}>
                  Estimated arrival
                </Text>
                <Text fontSize="14px" fontWeight="600" color={ff.ink}>
                  {new Date(detail.estimatedArrival).toLocaleDateString('en-US', {
                    month: 'short',
                    day: 'numeric',
                    year: 'numeric',
                  })}
                </Text>
              </Flex>
            )}
          </VStack>
          <Text fontSize="13px" color={ff.text2}>
            {formatWithdrawalSubtitle(detail.status)}
          </Text>
          {detail.failureMessage && (
            <Box bg="red.50" borderRadius="14px" p={3}>
              <Text fontSize="14px" color={ff.danger}>
                {detail.failureMessage}
              </Text>
            </Box>
          )}
        </VStack>
      ) : null}
    </ModalShell>
  );
};

export default WalletPayoutDetailModal;
